import React, { useEffect, useState } from 'react';
import { contract, fmt, chainNow } from '../lib';

const pad = (n) => String(n).padStart(2, '0');
export function countdown(sec) {
  if (sec <= 0) return '0s';
  const d = Math.floor(sec / 86400); const h = Math.floor((sec % 86400) / 3600); const m = Math.floor((sec % 3600) / 60); const s = Math.floor(sec % 60);
  return d > 0 ? `${d}d ${pad(h)}h ${pad(m)}m ${pad(s)}s` : `${pad(h)}h ${pad(m)}m ${pad(s)}s`;
}

// Live emission season: number, countdown to the next season drop, ORE/day now and next, and (optionally) the user's fleet share.
export default function SeasonPanel({ fleet }) {
  const [sch, setSch] = useState(null);
  const [offset, setOffset] = useState(0); // chain time - browser time
  const [tick, setTick] = useState(Date.now() / 1000);

  useEffect(() => {
    const r = contract('EmissionReserve'); const f = contract('Fleet'); const g = contract('Genesis');
    let alive = true;
    const load = async () => {
      // emission starts at pool launch (liveParams().start = 0 until Genesis.openPool); before that, count down to mintEnd
      const [p, cn, tp, mintEnd] = await Promise.all([r.liveParams(), chainNow(), f.totalPower(), g.mintEnd()]);
      const launched = Number(p.start) !== 0;
      const start = launched ? Number(p.start) : Number(mintEnd); const len = Number(p.seasonLength); const max = Number(p.maxSeasons);
      const idx = cn <= start ? 0 : Math.min(Math.floor((cn - start) / len), max);
      const [rate, next, cum, total] = await Promise.all([r.rateAt(idx), r.rateAt(idx + 1), r.cumulative(cn), r.totalScheduled()]);
      if (!alive) return;
      setOffset(cn - Date.now() / 1000);
      setSch({ start, len, max, idx, rate, next, cum, total, tp, cumAt: cn, launched });
    };
    load().catch(() => {});
    const t = setInterval(() => setTick(Date.now() / 1000), 1000);
    const re = setInterval(() => load().catch(() => {}), 60000);
    return () => { alive = false; clearInterval(t); clearInterval(re); };
  }, []);

  if (!sch) return <div className="card" data-testid="season" style={{ marginTop: '1rem' }}><p>Loading emission season…</p></div>;
  const now = tick + offset;
  const before = !sch.launched || now < sch.start;
  const waitingPool = !sch.launched && now >= sch.start; // mint ended, pool not opened yet
  const idx = before ? 0 : Math.floor((now - sch.start) / sch.len);
  const ended = idx >= sch.max;
  const nextDrop = before ? sch.start : sch.start + (idx + 1) * sch.len;
  const stale = !before && !ended && idx !== sch.idx; // a season boundary passed since the last read
  const rate = stale ? (sch.next) : sch.rate;
  const nextRate = stale ? (sch.next * 7n) / 10n : sch.next; // refreshed from chain within 60 s
  const emitted = sch.cum + (before || ended ? 0n : (rate * BigInt(Math.max(0, Math.floor(now - sch.cumAt)))) / 86400n);
  const pct = sch.total > 0n ? Number((emitted * 10000n) / sch.total) / 100 : 0;
  const share = fleet && sch.tp > 0n ? Number((fleet.power * 1000000n) / sch.tp) / 1e6 : 0;
  const myDay = fleet && sch.tp > 0n ? (rate * fleet.power) / sch.tp : 0n;
  const days = sch.len / 86400;
  const lenTxt = days >= 1 ? `${days}-day` : `${Math.round(sch.len / 60)}-min`;

  return (
    <div className="card" data-testid="season" style={{ marginTop: '1rem' }}>
      <h3>{ended ? 'Emission finished' : before ? 'Season 1 starts soon' : `Season ${idx + 1} of ${sch.max}`}</h3>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', marginTop: '.6rem' }}>
        {!ended && <div><p style={{ color: 'var(--muted)', fontSize: '.8rem' }}>{waitingPool ? 'Mining starts when the pool opens' : before ? (sch.launched ? 'Mining starts in' : 'Mining starts at pool launch (sell-out or mint end) in') : `Season ${idx + 2 > sch.max ? '—' : idx + 2} drop in`}</p><p className="stat" data-testid="season-countdown" style={{ fontSize: '1.3rem' }}>{waitingPool ? 'now' : countdown(nextDrop - now)}</p></div>}
        <div><p style={{ color: 'var(--muted)', fontSize: '.8rem' }}>Live rate</p><p className="price" data-testid="season-rate" style={{ fontSize: '1.3rem' }}>{fmt(before ? sch.rate : ended ? 0n : rate, 0)} ORE/day</p></div>
        {!ended && <div><p style={{ color: 'var(--muted)', fontSize: '.8rem' }}>Next season (×0.7)</p><p className="stat" data-testid="season-next" style={{ fontSize: '1.3rem' }}>{fmt(before ? sch.next : nextRate, 0)} ORE/day</p></div>}
        <div><p style={{ color: 'var(--muted)', fontSize: '.8rem' }}>Mined so far (all players)</p><p className="stat" style={{ fontSize: '1.3rem' }}>{fmt(emitted, 0)} ORE · {pct.toFixed(2)}%</p></div>
      </div>
      {fleet && (
        <div data-testid="season-fleet" style={{ display: 'flex', flexWrap: 'wrap', gap: '1.5rem', marginTop: '.8rem', borderTop: '1px solid var(--line)', paddingTop: '.6rem' }}>
          <div><p style={{ color: 'var(--muted)', fontSize: '.8rem' }}>Your fleet power</p><p className="stat">{String(fleet.power)} · {(share * 100).toFixed(2)}% of fleet</p></div>
          <div><p style={{ color: 'var(--muted)', fontSize: '.8rem' }}>Your mining now</p><p className="price">≈{fmt(myDay, 0)} ORE/day</p></div>
          <div><p style={{ color: 'var(--muted)', fontSize: '.8rem' }}>Claimable now (gross)</p><p className="price" data-testid="fleet-claimable">{fmt(fleet.claimable, 0)} ORE</p></div>
          <div><p style={{ color: 'var(--muted)', fontSize: '.8rem' }}>Mined &amp; paid to you</p><p className="stat" data-testid="fleet-mined">{fleet.mined === null ? '…' : `${fmt(fleet.mined, 0)} ORE`}</p></div>
        </div>
      )}
      <p style={{ color: 'var(--muted)', fontSize: '.8rem', marginTop: '.6rem' }}>{lenTxt} seasons{!ended ? ` · this season pays ${fmt(((before ? sch.rate : rate) * BigInt(sch.len)) / 86400n, 0)} ORE in total` : ''}; each season's rate is 70% of the previous one. Times use chain time.</p>
    </div>
  );
}
